import fetch from "cross-fetch";
import * as cheerio from 'cheerio';
import { isBrowser } from "browser-or-node";
import { Agent } from "https";

import { Helper } from "../helpers/helper";
// this import is vital for keep alive agents to be applied to the fetch implementation
import { ImisAccessContext, ImisAuthentication, ImisCredentials, ImisRequestVerificationToken, ImisResponse, ImisResponseItems } from "../models/imis.models";
import { PayloadUtility } from "./payload.utility";
import { UrlUtility } from './url.utility';
import { ContextUtility } from './context.utility';

/**
 * Low-level REST client for the iMIS API, handling authentication (browser passthrough vs Node OAuth/anonymous), request execution, keep-alive connections, and offset/limit pagination.
 */
export class RestUtility {

    private static environments: { [key: string]: ImisCredentials };

    public static baseUrl: string;
    public static authentication: ImisAuthentication;
    public static httpsAgent: Agent;

    //#region ENVIRONMENTS

    /**
     * Registers a named collection of iMIS environment credentials for later activation via {@link setEnvironmentByName}.
     * @param environments map of environment name to its iMIS URL/credentials
     */
    public static setEnvironments(environments: { [key: string]: ImisCredentials }) {
        this.environments = environments;
    }

    /**
     * Looks up a previously registered environment by name and activates it as the current connection.
     * @param environmentName the key of the environment to activate, as registered via {@link setEnvironments}
     */
    public static setEnvironmentByName(environmentName: string) {
        let { url, username, password, refresh_token } = this.environments[environmentName];
        this.setEnvironment(url, username, password, refresh_token);
    }

    /**
     * Configures the active iMIS base URL and authentication used by subsequent requests. When both a username and password are supplied, authenticated (OAuth) access is configured; otherwise authentication is cleared so requests are made anonymously.
     * @param baseUrl the iMIS site base URL to target
     * @param username optional username for authenticated access; omitted for anonymous access
     * @param password optional password for authenticated access; omitted for anonymous access
     * @param refresh_token optional refresh token used to renew access without re-sending username/password
     */
    public static setEnvironment(baseUrl: string, username?: string, password?: string, refresh_token?: string) {
        this.baseUrl = baseUrl;
        if (!Helper.isStringNullOrEmpty(username) && !Helper.isStringNullOrEmpty(password)) {
            this.authentication = new ImisAuthentication(username, password, refresh_token);
        } else {
            this.authentication = null;
        }
    }

    /**
     * Creates and stores an HTTPS keep-alive agent used for Node-side requests, reducing per-request connection overhead (and helping avoid SNAT port exhaustion, e.g. in Azure Functions).
     */
    public static initializeNodeKeepAlive() {
        // Lazy-load https only in Node (keeps browser bundles clean)
        // azure functions limits max sockets to around 120 before get SNAT port exhaustion
        this.httpsAgent = new Agent({ keepAlive: true, timeout: 60000, maxSockets: 100, maxFreeSockets: 20 });
    }

    //#endregion

    //#region REST METHODS

    /**
     * Performs a single GET request against the iMIS API. Errors are caught and logged to the console rather than thrown.
     * @param url endpoint path to request (appended to the resolved base URL)
     * @param params optional object of query string parameters to append to the url
     * @param rawResponse when true, skip iMIS payload parsing and return the raw JSON as returned by iMIS
     * @returns the parsed (or raw) response data, or null if the request failed
     */
    // TODO: REVISE THIS FOR PROPER TYPING on RAW RESPONSE CASE
    public static async restApiGetData<T = ImisResponseItems>(url: string, params?: any, rawResponse?: boolean): Promise<T> {
        let data: T = null;

        let requestUrl = UrlUtility.getUrlStringWithParams(url, params);
        try {
            let response = await this.executeFetchRequest(requestUrl);

            data = await this.getJsonResponseData(response, rawResponse);
        } catch (error) {
            console.error(`Error encountered when performing the following request ${requestUrl}: ${error}`);
        }

        return data;
    }

    /**
     * Repeatedly performs GET requests against the iMIS API, following iMIS's offset/limit pagination (using `HasNext`/`NextOffset` from each page) to accumulate every matching record into a single items collection.
     * @param url endpoint path to request
     * @param params optional query parameters; `offset` and `limit` default to `0` and `500` respectively and are then overwritten as pagination progresses
     * @param rawResponse when true, skip iMIS payload parsing and accumulate items via the raw `$values` array instead of `Items`
     * @param maximumRecords optional cap on the total number of records retrieved; once reached, no further pages are requested
     * @returns the response from the last successful page with its `Items` replaced by the full aggregated set of records, or null if the first page failed
     */
    // TODO: REVISE THIS FOR PROPER TYPING on RAW RESPONSE CASE
    public static async restApiGetDataAll<T = ImisResponse>(url: string, params?: any, rawResponse?: boolean, maximumRecords?: number): Promise<ImisResponseItems<T>> {
        let data: ImisResponseItems<T> = null;

        try {
            let requestParams = Object.assign({}, params);
            requestParams.offset = requestParams.offset || 0;
            requestParams.limit = requestParams.limit || 500;

            let responseItems: T[] = [];
            do {
                // set parameters if available
                if (data != null) {
                    requestParams.offset = data.NextOffset;
                }
                if (maximumRecords != null) {
                    let remainingRecords = maximumRecords - responseItems.length;
                    requestParams.limit = Math.min(requestParams.limit, remainingRecords);
                }

                // check whether limit is 0 which means we have gotten the maximum number of records required
                if (requestParams.limit == 0) {
                    break;
                }

                data = await this.restApiGetData(url, requestParams, rawResponse);
                if (data != null) {
                    responseItems = responseItems.concat(rawResponse ? (<any>data.Items).$values : data.Items);
                } else if (responseItems.length == 0) {
                    throw "Could not retrieve data";
                } else {
                    // return what you already have
                    break;
                }
            } while (data.HasNext);

            if (rawResponse) {
                (<any>data.Items).$values = responseItems;
            } else {
                data.Items = responseItems;
            }
        } catch (error) {
            console.error(error);
        }

        return data;
    }

    /**
     * Sends a POST or PUT request with a JSON body to the iMIS API, retrying up to 3 times if a valid response could not be obtained/parsed.
     * @param url endpoint path to send the request to
     * @param payloadData request body, serialized as JSON
     * @param method HTTP method to use; defaults to "POST"
     * @param rawResponse when true, skip iMIS payload parsing and return the raw JSON as returned by iMIS
     * @returns the parsed (or raw) response data, or null if all attempts failed
     */
    public static async restApiSendData<T = ImisResponseItems>(url: string, payloadData: any, method: "POST" | "PUT" = "POST", rawResponse?: boolean): Promise<T> {
        let parsedResponseData: T = null;
        let retryCount = 0;

        while (parsedResponseData == null && retryCount < 3) {
            try {
                let response = await this.executeFetchRequest(url, method, payloadData);
                parsedResponseData = await this.getJsonResponseData(response, rawResponse);
            } catch (error) {
                console.error(`Error encountered when performing the following request (attempt #${retryCount + 1}) ${url}: ${error}`);
            }

            retryCount++;
        }

        return parsedResponseData;
    }

    /**
     * Sends a DELETE request to the iMIS API for the given endpoint.
     * @param url endpoint path of the record/resource to delete
     * @returns true if the delete succeeded, false if an error occurred
     */
    public static async restApiDeleteData(url: string): Promise<boolean> {
        let dataDeleted: boolean = false;

        try {
            await this.executeFetchRequest(url, "DELETE", null);
            dataDeleted = true;
        } catch (error) {
            console.error(error);
        }

        return dataDeleted;
    }

    /**
     * Requests an OAuth access token from iMIS's `/token` endpoint using the configured authentication credentials. When authenticating via a refresh token, the stored refresh token is updated with the new one returned by iMIS for use on subsequent calls.
     * @returns the access context (access token, expiry, and related OAuth fields) returned by iMIS
     */
    public static async getAccessContext(): Promise<ImisAccessContext> {
        let url = this.getBaseUrl() + "/token";

        let payload: any;
        let isRefreshToken: boolean = this.authentication.grant_type == "refresh_token";
        if (isRefreshToken) {
            let { grant_type, username: client_id, password: client_secret, refresh_token } = this.authentication;
            payload = { grant_type, client_id, client_secret, refresh_token };
        } else {
            payload = { ...this.authentication };
        }

        let response = await fetch(url, {
            method: "POST",
            body: new URLSearchParams(payload)
        });
       let accessContext: ImisAccessContext = await response.json();

        // update the refresh token for upcoming access requests
        if (isRefreshToken) {
            this.authentication.refresh_token = accessContext.refresh_token;
        }

        return accessContext;
    }

    //#endregion

    //#region TOKENS ACCESS METHODS

    /**
     * Reads iMIS's anti-forgery `__RequestVerificationToken` value injected into the current page's DOM, used for browser-side passthrough authentication.
     * @returns the token value, or an empty string if the element is not present on the page
     */
    public static getRequestVerificationToken(): string {
        let result = "";

        // Passthrough authentication - get the __RequestVerificationToken that is injected into the page by the iMIS site.
        let element: HTMLInputElement = <HTMLInputElement>document.getElementById("__RequestVerificationToken");
        if (element != null) {
            result = element.value;
        }

        return result;
    }

    /**
     * Obtains a fresh OAuth access token from iMIS.
     * @returns the access token string
     */
    public static async getAccessToken(): Promise<string> {
        let accessContext = await this.getAccessContext();
        return accessContext.access_token;
    }

    /**
     * Fetches the iMIS site's home page anonymously (Node-only anonymous access path) and scrapes the anti-forgery verification token from both the page HTML and the `set-cookie` response header.
     * @returns object containing the header and cookie token values (empty strings if not found, or if an error occurred)
     */
    public static async getAnonymousRequestVerificationToken(): Promise<ImisRequestVerificationToken> {
        let headerToken = "";
        let cookieToken = "";

        try {
            let response = await fetch(this.getBaseUrl());
            let responseText = await response.text();

            // get the request verification token in the html
            let cHtml = cheerio.load(responseText);
            headerToken = <string>cHtml("[id=__RequestVerificationToken]").val();

            // get the request verification token in the cookie header
            let setCookieHeader = response.headers.get("set-cookie");
            let tokenMatchResults = setCookieHeader.match(/__RequestVerificationToken=(.+?);/);
            if (tokenMatchResults.length > 1) {
                cookieToken = tokenMatchResults[tokenMatchResults.length - 1];
            }
        } catch (error) {
            console.error(error);
        }

        return {
            cookie: cookieToken,
            header: headerToken
        };
    }

    //#endregion

    /**
     * Resolves the full request URL and configuration (headers, auth, agent) and performs the fetch, throwing if the response is not ok.
     * @param url endpoint path, appended to the resolved base URL
     * @param method HTTP method to use; defaults to "GET"
     * @param body optional request body, JSON-stringified before sending; omitted for GET/no-body requests
     * @returns the raw fetch Response
     */
    private static async executeFetchRequest(url: string, method: "GET" | "POST" | "PUT" | "DELETE" = "GET", body?: any): Promise<Response> {
        let requestUrl = this.getBaseUrl() + url;
        let requestConfig = await this.getRequestConfig(method, body);
        let response = <Response>await fetch(requestUrl, <any>requestConfig);
        this.ensureResponseOk(response);

        return response;
    }

    /**
     * Validates that a fetch response completed successfully, throwing the HTTP status code otherwise.
     * @param response fetch Response to validate
     */
    private static ensureResponseOk(response: Response) {
        if (!response.ok) throw response.status;
    }

    /**
     * Parses a fetch response body as JSON and, unless raw data was requested, converts it from iMIS's verbose `$type`/`$value` payload format into a plain object via {@link PayloadUtility.parseImisPayload}.
     * @param response fetch Response whose body should be parsed
     * @param rawResponse when true, return the parsed JSON as-is without iMIS payload parsing
     * @returns the parsed (or raw) response data
     */
    private static async getJsonResponseData<T>(response: Response, rawResponse?: boolean): Promise<T> {
        let responseData = await response.json();
        if (!rawResponse) {
            responseData = PayloadUtility.parseImisPayload<T>(responseData);
        }

        return responseData;
    }

    /**
     * Builds the fetch `RequestInit` for a request, branching on browser vs Node execution: in a browser it attaches the page's `RequestVerificationToken` header; in Node it attaches a Bearer access token when authenticated, or an anonymous verification token/cookie when not, and applies the shared keep-alive HTTPS agent when one has been configured.
     * @param method HTTP method for the request
     * @param body optional request payload, JSON-stringified into the request body when provided
     * @returns the configured RequestInit ready to pass to fetch
     */
    private static async getRequestConfig(method: string, body?: any): Promise<RequestInit> {
        let headers = {
            "Content-Type": "application/json"
        };

        if (isBrowser) {
            let requestVerificationToken: string = this.getRequestVerificationToken();
            if (!Helper.isStringNullOrEmpty(requestVerificationToken)) {
                headers["RequestVerificationToken"] = requestVerificationToken;
            }
        } else {
            // most likely node, check whether accessing as anonymous user
            if (this.authentication != null) {
                let accessToken: string = await this.getAccessToken();
                headers["Authorization"] = `Bearer ${accessToken}`;
            } else {
                let requestVerificationToken: ImisRequestVerificationToken = await this.getAnonymousRequestVerificationToken();
                headers["RequestVerificationToken"] = requestVerificationToken.header;
                headers["Cookie"] = `__RequestVerificationToken=${requestVerificationToken.cookie}`;
            }
        }

        let requestConfig: RequestInit = {
            method,
            headers,
            body: body == null ? body : JSON.stringify(body)
        };
        if (!isBrowser && this.httpsAgent != null) {
            requestConfig["agent"] = this.httpsAgent;
        }
        return requestConfig;
    }

    /**
     * Resolves the iMIS base URL to use for requests: in a browser it is read from the current page's client context; in Node it is the URL configured via {@link setEnvironment}/{@link setEnvironmentByName}. A trailing slash, if present, is stripped.
     * @returns the normalized base URL, without a trailing slash
     */
    private static getBaseUrl(): string {
        let baseUrl: string = "";

        if (isBrowser) {
            let context = ContextUtility.getImisClientContext();
            baseUrl = context.baseUrl;
        } else {
            // most likely node
            baseUrl = this.baseUrl;
        }

        if (baseUrl.endsWith("/")) {
            baseUrl = baseUrl.substring(0, baseUrl.length - 1);
        }

        return baseUrl;
    }

}